import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faBarcode } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { SignalStringFieldBaseDirective } from '../signal-field-base.directive';

/**
 * Low-level single-line text input for signal forms.
 *
 * Everything store- and validation-related sits in `SignalStringFieldBaseDirective`; this
 * class adds the presentation extras (placeholder, unit label, `inputmode`) and the
 * barcode scanner:
 *
 * ```html
 * <pace-core-text-field [formField]="signalForm.textValue" ariaLabel="Report text" />
 * ```
 *
 * Unlike the date and time fields, the length and pattern constraints need no
 * inputs of their own: `minLength`, `maxLength` and `pattern` are part of the
 * `FormUiControl` contract, so the schema's `minLength()`/`maxLength()`/`pattern()`
 * rules reach the native input on their own.
 *
 * Multi-line text lives in `MultilineTextFieldComponent`, which shares the same base.
 */
@Component({
    selector: 'pace-text-field',
    templateUrl: './text-field.component.html',
    styleUrls: ['./text-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FaIconComponent, TranslateModule],
})
export class TextFieldComponent extends SignalStringFieldBaseDirective {
    // --- `FormUiControl` inputs, all bound automatically by `[formField]` -----

    public readonly minLength = input<number | undefined>(undefined);
    public readonly maxLength = input<number | undefined>(undefined);
    public readonly pattern = input<readonly RegExp[]>([]);

    // --- own inputs ----------------------------------------------------------

    public readonly placeholder = input('');

    /** optional unit label rendered after the input, e.g. 'MHz' */
    public readonly unit = input('');

    /** native `inputmode` hint, which decides the on-screen keyboard on mobile */
    public readonly inputMode = input('');

    // --- barcode scanning ----------------------------------------------------

    public readonly scanFn = input<(() => Observable<string>) | undefined>(undefined);

    /** shows the scan button */
    public readonly scannable = computed(() => this.scanFn() !== undefined);

    /** the processed scan result, for consumers that need to react beyond the value change */
    public readonly scanResults = output<string>();

    /**
     * The schema's first pattern, as the native `pattern` attribute expects it.
     * Only a hint for assistive tech and native validation — the schema rule is
     * what actually reports.
     */
    public readonly patternAttribute = computed(() => this.pattern()[0]?.source);

    public readonly faBarcode = faBarcode;

    private readonly destroyRef = inject(DestroyRef);

    /**
     * Opens the scanner and writes the processed result into the field. The value is
     * set through the same model the user's typing goes through, so the bound field
     * revalidates exactly as it would after manual input; `touched` is set too,
     * because a scan is an interaction even though no blur happens.
     */
    public scan(): void {
        const fn = this.scanFn();
        if (!fn) {
            return;
        }
        fn()
            .pipe(
                take(1),
                tap((result) => {
                    this.value.set(result);
                    this.touched.set(true);
                    this.scanResults.emit(result);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }
}
